import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import cardHeader from "../../../../assets/image/magazine/card-header.svg";
import { formatArticleDate } from "./articleMagazineFormatters";
import { toRem } from "./articleMagazinePreviewTokens";

/**
 * Keep in sync with FE_Application `articlePreviewCardLayout.js`.
 */
export const ARTICLE_PREVIEW_CARD_TUNING = {
  card: {
    width: 360,
    borderRadius: 4,
  },
  header: {
    height: 54,
    width: 360,
  },
  headerCategoryLabel: {
    left: 14,
    top: 13,
    fontSize: 15,
    fontWeight: 700,
    maxWidth: 220,
  },
  headerDateLabel: {
    right: 16,
    top: 14,
    fontSize: 15,
    fontWeight: 400,
    maxWidth: 120,
  },
  image: {
    widthRatio: 1,
    aspectRatio: "360 / 200",
    overlapTop: 16,
  },
  body: {
    padding: 16,
    gap: 8,
    titleFontSize: 20,
    titleLineHeight: 1.2,
    subtitleFontSize: 17,
    subtitleLineHeight: 23,
  },
};

const T = ARTICLE_PREVIEW_CARD_TUNING;

export const articlePreviewCardRootSx = {
  display: "flex",
  flexDirection: "column",
  width: toRem(T.card.width),
  maxWidth: "100%",
  flexShrink: 0,
  borderRadius: toRem(T.card.borderRadius),
  overflow: "hidden",
  backgroundColor: "#ffffff",
  textDecoration: "none",
  color: "inherit",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
  transition: "box-shadow 0.2s ease",
  boxSizing: "border-box",
  "&:hover": {
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.12)",
  },
};

export const ArticlePreviewCardView = ({ article }) => (
  <>
    <Box
      sx={{
        position: "relative",
      }}
    >
      <Box
        component="img"
        src={cardHeader}
        alt=""
        aria-hidden="true"
        sx={{
          width: toRem(T.header.width),
          height: toRem(T.header.height),
          display: "block",
          objectFit: "cover",
        }}
      />
      {article.category ? (
        <Typography
          sx={{
            position: "absolute",
            left: toRem(T.headerCategoryLabel.left),
            top: toRem(T.headerCategoryLabel.top),
            maxWidth: toRem(T.headerCategoryLabel.maxWidth),
            color: "#054297",
            fontWeight: T.headerCategoryLabel.fontWeight,
            fontSize: toRem(T.headerCategoryLabel.fontSize),
            textTransform: "uppercase",
            lineHeight: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {article.category}
        </Typography>
      ) : null}
      {formatArticleDate(article.publishedAt) ? (
        <Typography
          sx={{
            position: "absolute",
            right: toRem(T.headerDateLabel.right),
            top: toRem(T.headerDateLabel.top),
            maxWidth: toRem(T.headerDateLabel.maxWidth),
            color: "#054297",
            fontWeight: T.headerDateLabel.fontWeight,
            fontSize: toRem(T.headerDateLabel.fontSize),
            lineHeight: 1,
            textAlign: "right",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {formatArticleDate(article.publishedAt)}
        </Typography>
      ) : null}
    </Box>

    <Box
      component="img"
      src={article.image}
      alt=""
      sx={{
        width: `${T.image.widthRatio * 100}%`,
        aspectRatio: T.image.aspectRatio,
        objectFit: "cover",
        display: "block",
        flexShrink: 0,
        mt: toRem(-T.image.overlapTop),
      }}
    />

    <Box
      sx={{
        p: toRem(T.body.padding),
        display: "flex",
        flexDirection: "column",
        gap: toRem(T.body.gap),
        flex: 1,
        minHeight: 0,
        boxSizing: "border-box",
      }}
    >
      <Typography
        sx={{
          color: "#000000",
          fontWeight: 600,
          fontSize: toRem(T.body.titleFontSize),
          lineHeight: T.body.titleLineHeight,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {article.title}
      </Typography>
      {article.subtitle ? (
        <Typography
          sx={{
            color: "#000000",
            fontSize: toRem(T.body.subtitleFontSize),
            lineHeight: toRem(T.body.subtitleLineHeight),
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {article.subtitle}
        </Typography>
      ) : null}
    </Box>
  </>
);

ArticlePreviewCardView.propTypes = {
  article: PropTypes.shape({
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    image: PropTypes.string,
    category: PropTypes.string,
    publishedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};
